/* eslint-disable no-plusplus */
/* eslint-disable import/no-unresolved */
import { useEffect, useState, useCallback } from 'react';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';

import { GET_TERMS_OF_AGREEMENTS } from 'Apollo/Queries';
import { FETCH_USER } from 'Apollo/Queries/Auth';
import { getQueryParams } from 'utils';
import { ACCEPT_TERMS_OF_AGREEMENTS, IAM_REGISTER_USER } from 'Apollo/Mutations';
import Signup from '../components/Signup';

const productName = process.env.REACT_APP_PRODUCT;

const SignupContainer = () => {
    const history = useHistory();
    const params = getQueryParams();
    const [userData, setUserData] = useState(null);
    const [hasAccount, setHasAccount] = useState(false);
    const [email, setEmail] = useState('');
    const [registrationToken, setRegistrationToken] = useState('');
    const [organizationId, setOrganizationId] = useState('');
    const [firstName, setFirstName] = useState('');
    const [firstNameError, setFirstNameError] = useState(true);
    const [lastName, setLastName] = useState('');
    const [lastNameError, setLastNameError] = useState(true);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [phoneNumberError, setPhoneNumberError] = useState(true);
    const [password, setPassword] = useState('');
    const [termsAndConditionCheck, setTermsAndConditionCheck] = useState(false);
    const [passwordShown, setPasswordShown] = useState(false);
    const [disableBtn, setDisableBtn] = useState(true);
    const [passwordError, setPasswordError] = useState(true);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [termsModal, setTermsModal] = useState(false);
    const [privacyModal, setPrivacyModal] = useState(false);

    /* apollo query to fetch the user details using email and organizationId */
    const [fetchUser, { data: fetchUserData }] = useLazyQuery(FETCH_USER, {
        variables: {
            input: {
                email,
                organizationId,
            },
        },
    });

    /* apollo query to fetch the terms of agreement */
    const termsOfAgreement = useQuery(GET_TERMS_OF_AGREEMENTS);

    /* apollo mutation to add acceptance of terms of agreement */
    const [acceptTermsOfAgreements] = useMutation(ACCEPT_TERMS_OF_AGREEMENTS, {
        variables: {
            input: {
                termsId: termsOfAgreement.data && termsOfAgreement.data.getTermsOfAgreements.termsId,
                email,
            },
        },
    });

    /* apollo mutation to register new user in IAM service */
    const [iamRegisterUser, { loading: registerUserLoading }] = useMutation(IAM_REGISTER_USER, {
        variables: {
            input: {
                email,
                firstName,
                lastName,
                password,
                registrationToken,
                product: productName,
                mobileNumber: phoneNumber.replace(/\D/g, ''),
            },
        },
    });

    /* function to format the input phone number */
    const handlePhoneInput = val => {
        let updatedValue;
        if (!val) {
            updatedValue = val;
        } else {
            updatedValue = val.replace(/\D/g, '');
        }
        setPhoneNumber(updatedValue);
    };

    /* callback function called when user click on Save button of user registration form */
    const onSave = async () => {
        setShowErrorAlert(false);
        try {
            const [_iamRegisterRes, acceptTermsRes] = await Promise.all([iamRegisterUser(), acceptTermsOfAgreements()]);
            if (acceptTermsRes.data.acceptTermsOfAgreements && _iamRegisterRes.data.IAMregisterUser.status === 'SUCCESS') {
                history.push('/register-success');
            } else {
                setErrorMsg('An error occurred while registering the user');
                setShowErrorAlert(true);
            }
        } catch (e) {
            let errorMessage;
            if (e.message.includes('already activated')) {
                errorMessage = 'User has already been created. Please contact administrator';
            } else if (e.message.includes('The user is already registered for the organization with the specified product.')
            ) {
                errorMessage = 'You are already registered with us! Please login directly.';
            } else if (e.message.includes('Invalid Registration Token')) {
                errorMessage = 'Invalid registration token. Please contact administrator';
            } else if (e.message.includes('Password Policy Requirement')) {
                errorMessage = 'Password is not complex enough. Please include 1 uppercase, 1 special character, and length > 8';
            } else {
                errorMessage = 'Error: Please contact administrator';
            }
            setErrorMsg(errorMessage);
            setShowErrorAlert(true);
        }
    };

    /* function to check upper case validity of password */
    const checkUpperCase = useCallback(() => {
        const upperCaseLetters = /[A-Z]/g;
        if (password && password.trim() !== '' && password.match(upperCaseLetters)) {
            return true;
        }
        return false;
    }, [password]);

    /* function to check lower case validity of password */
    const checkLowerCase = useCallback(() => {
        const lowerCaseLetters = /[a-z]/g;
        if (password && password.trim() !== '' && password.match(lowerCaseLetters)) {
            return true;
        }
        return false;
    }, [password]);

    /* function to check number validity of password */
    const checkNumber = useCallback(() => {
        const numbers = /[0-9]/g;
        if (password && password.trim() !== '' && password.match(numbers)) {
            return true;
        }
        return false;
    }, [password]);

    /* function to check special character validity of password */
    const checkSpecialChar = useCallback(() => {
        const specialChars = /[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/;
        if (password && password.trim() !== '' && password.match(specialChars)) {
            return true;
        }
        return false;
    }, [password]);

    /* function to check length validity of password */
    const checkLength = useCallback(() => {
        const minLength = 8;
        const maxLength = 20;
        if (password && password.trim() !== '' && password.length >= minLength && password.length <= maxLength) {
            return true;
        }
        return false;
    }, [password]);

    /* function to check no first name or last name validity of password */
    const checkName = useCallback(() => {
        if (password && password.trim() !== '' && password !== firstName && password !== lastName) {
            return true;
        }
        return false;
    }, [password]);

    /* side effect to set email, registrationToken and organizationId from url paramaters of registration url */
    useEffect(() => {
        if (params && (email !== params.email || registrationToken !== params.registrationToken || organizationId !== params.orgId)) {
            setEmail(params.email);
            setRegistrationToken(params.registrationToken);
            setOrganizationId(params.orgId);
        }
    }, [params]);

    /* side effect to call fetchUser query when organizationId is changed */
    useEffect(() => {
        fetchUser();
    }, [organizationId]);

    /* side effect to set userData and hasAccount state variable when fetchUserData is available */
    useEffect(() => {
        if (fetchUserData?.IAMfetchUser && fetchUserData.IAMfetchUser.user.isProfileCompleted && fetchUserData.IAMfetchUser.user.registrationStatus === 'REGISTERED') {
            setUserData(fetchUserData.IAMfetchUser.user);
            setHasAccount(true);
        }
    }, [fetchUserData]);

    /* side effect when firstName is changed */
    useEffect(() => {
        if (firstName !== undefined && firstName.trim() !== '') {
            setFirstNameError(false);
        } else {
            setFirstNameError(true);
        }
    }, [firstName]);

    /* side effect when lastName is changed */
    useEffect(() => {
        if (lastName !== undefined && lastName.trim() !== '') {
            setLastNameError(false);
        } else {
            setLastNameError(true);
        }
    }, [lastName]);

    /* side effect when phone number is changed */
    useEffect(() => {
        const maxPhoneNumberLength = 14;
        if (phoneNumber !== undefined && phoneNumber.trim().length <= maxPhoneNumberLength && phoneNumber.trim() !== '') {
            setPhoneNumberError(false);
        } else {
            setPhoneNumberError(true);
        }
    }, [phoneNumber]);

    /* side effect when any input error happens */
    useEffect(() => {
        const shouldDisableBtn = (firstNameError || lastNameError || phoneNumberError || passwordError || !termsAndConditionCheck);
        if (shouldDisableBtn) {
            setDisableBtn(true);
        } else {
            setDisableBtn(false);
        }
    }, [firstNameError, lastNameError, phoneNumberError, passwordError, termsAndConditionCheck]);

    /* side effect when password is changed */
    useEffect(() => {
        const validPassword = (checkUpperCase() && checkLowerCase() && checkNumber() && checkSpecialChar() && checkLength() && checkName());
        if (validPassword) {
            setPasswordError(false);
        } else {
            setPasswordError(true);
        }
    }, [password]);

    return (
        <Signup
            email={email}
            setEmail={setEmail}
            firstName={firstName}
            setFirstName={setFirstName}
            lastName={lastName}
            setLastName={setLastName}
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            password={password}
            setPassword={setPassword}
            termsAndConditionCheck={termsAndConditionCheck}
            setTermsAndConditionCheck={setTermsAndConditionCheck}
            passwordShown={passwordShown}
            setPasswordShown={setPasswordShown}
            disableBtn={disableBtn}
            onSave={onSave}
            passwordError={passwordError}
            handlePhoneInput={handlePhoneInput}
            setShowErrorAlert={setShowErrorAlert}
            errorMsg={errorMsg}
            showErrorAlert={showErrorAlert}
            registerUserLoading={registerUserLoading}
            termsModal={termsModal}
            setTermsModal={setTermsModal}
            privacyModal={privacyModal}
            setPrivacyModal={setPrivacyModal}
            userData={userData}
            hasAccount={hasAccount}
        />
    );
};

export default SignupContainer;
